import { getCookie } from './utils';

import tinymce from 'tinymce/tinymce';

// Import the Silver theme
import 'tinymce/themes/silver/theme';

// Import the default icons
import 'tinymce/icons/default/icons';

// Import required plugins (add more as needed)
import 'tinymce/plugins/image'; // Essential for image button and upload
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/code'; // View source code
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';
import 'tinymce/skins/content/default/content.css';
import 'tinymce/skins/ui/oxide/skin.css'; // The UI skin CSS

const imageUploadHandler = (blobInfo, progress) =>
    new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', blobInfo.blob(), blobInfo.filename());

        const csrfToken = getCookie('csrftoken');
        if (!csrfToken) {
            console.error('CSRF token not found. Cannot upload image.');
            reject({ message: 'CSRF token not found.', remove: true }); // remove:true tells TinyMCE to remove the placeholder
            return;
        }

        fetch('/api/files/upload/', {
            method: 'POST',
            headers: {
                'X-CSRFToken': csrfToken,
            },
            body: formData,
        })
            .then((response) => {
                if (!response.ok) {
                    return response.text().then((text) => {
                        let errorMsg = `HTTP Error: ${response.status}`;
                        try {
                            const errorJson = JSON.parse(text);
                            errorMsg =
                                errorJson.detail ||
                                errorJson.message ||
                                errorMsg;
                        } catch (e) {}
                        throw new Error(errorMsg);
                    });
                }
                return response.json();
            })
            .then((data) => {
                if (!data || !data.file) {
                    console.error(
                        "Image upload successful, but response JSON did not contain a 'url' property.",
                        data,
                    );
                    reject({
                        message:
                            'Invalid response format from server. Missing URL.',
                        remove: true,
                    });
                    return;
                }
                resolve(data.file);
            })
            .catch((error) => {
                console.error('Image upload failed:', error);
                reject({
                    message: `Image upload failed: ${error.message}`,
                    remove: true,
                });
            });
    });

export default function initTiny() {
    const edit_links = document.querySelectorAll('.window-edit-link');

    function initializeTiny(selector, field_to_update) {
        return tinymce.init({
            selector: selector,
            menubar: false,
            plugins: [
                'image',
                'save',
                'lists',
                'link',
                'underline',
                'textcolor',
            ],
            toolbar1:
                'bold italic underline | alignleft aligncenter alignright alignjustify | fontsize styles forecolor backcolor numlist bullist',
            toolbar2: 'undo redo | link image | cancelBtn save',
            style_formats: [
                { title: 'Nagłówek 1', block: 'h1' },
                { title: 'Nagłówek 2', block: 'h2' },
                { title: 'Nagłówek 3', block: 'h3' },
                { title: 'Akapit', block: 'p' },
            ],
            font_size_formats: '12px 14px 16px 18px 24px 36px 48px',
            relative_urls: false,
            remove_script_host: true,
            convert_urls: true,
            license_key: 'gpl',
            base_url: '/static/tinymce',
            suffix: '.min',
            resize: false,
            statusbar: false,
            init_instance_callback: (editor) => {
                const init_html = editor
                    .getContainer()
                    .closest('.window-body')
                    .querySelector('.window-content').innerHTML;
                editor.setContent(init_html);
            },
            setup: (editor) => {
                editor.on('init', () => {
                    const container =
                        editor.getContainer().closest('.window-draggable') ||
                        null;
                    if (container) {
                        const height = container.offsetHeight - 70;
                        editor.getContainer().style.height = height + 'px';
                    }
                });
                editor.ui.registry.addButton('cancelBtn', {
                    icon: 'close',
                    tooltip: gettext('Wyczyść zmiany i zamknij edytor'),
                    onAction: (_) => {
                        const form = editor.formElement;
                        const container = form.closest('.window');
                        editor.setContent(
                            editor
                                .getContainer()
                                .closest('.window-body')
                                .querySelector('.window-content').innerHTML,
                        );
                        container
                            .querySelector('.window-edit-link')
                            .classList.remove('hidden');
                        container
                            .querySelector('.window-content')
                            .classList.remove('hidden');
                        !form.classList.contains('hidden')
                            ? form.classList.add('hidden')
                            : null;
                    },
                });
            },
            images_upload_handler: imageUploadHandler,
            save_onsavecallback: () => {
                const form_data = new FormData();
                form_data.append(
                    field_to_update,
                    tinymce.activeEditor.getContent(),
                );

                const data = {};
                data[field_to_update] = tinymce.activeEditor.getContent();

                fetch('/api/config/tips/', {
                    method: 'PATCH',
                    headers: {
                        'X-CSRFToken': getCookie('csrftoken'),
                    },
                    body: JSON.stringify(data),
                })
                    .then((response) => response.json())
                    .then((data) => {
                        const container = tinymce.activeEditor
                            .getElement()
                            .closest('form')
                            .closest('.window');
                        const content =
                            container.querySelector('.window-content');
                        content.innerHTML = tinymce.activeEditor.getContent();
                        content.classList.remove('hidden');
                        tinymce.activeEditor
                            .getElement()
                            .closest('form')
                            .classList.add('hidden');
                        container
                            .querySelector('.window-edit-link')
                            .classList.remove('hidden');
                        tinymce.activeEditor.startContent =
                            tinymce.activeEditor.getContent();
                    })
                    .catch((error) => {
                        console.error(`Update error: ${error}`);
                    });
            },
        });
    }

    const tiny_1 = initializeTiny('#id_tips_markings', 'tips_markings');
    const tiny_2 = initializeTiny('#id_tips_dimensions', 'tips_dimensions');
    const tiny_3 = initializeTiny('#id_tips_wastes', 'tips_wastes');
    const tiny_4 = initializeTiny(
        '#id_tips_component_classification',
        'tips_component_classification',
    );
    const tiny_5 = initializeTiny('#id_tips_dictionary', 'tips_dictionary');

    edit_links.forEach((link) => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            const container = link.closest('.window');
            const form = container.querySelector('.window-body form');
            const content = container.querySelector('.window-content');
            form.classList.contains('hidden')
                ? form.classList.remove('hidden')
                : null;
            !content.classList.contains('hidden')
                ? content.classList.add('hidden')
                : null;
            !link.classList.contains('hidden')
                ? link.classList.add('hidden')
                : null;
        });
    });
}
